import { useCallback } from "react";

import { useDebounce, useSwrInfiniteFetch } from "hooks";

import {
  CARRIER_SEARCH_DEBOUNCE_MS,
  CARRIER_SEARCH_PAGE_LIMIT,
} from "../constants";

import type { CarrierSearchResponse, CarrierSearchResult } from "../types";

const SEARCH_ERROR_MESSAGE = "Failed to search carriers.";

interface UseCarrierCompanySearchProps {
  searchValue: string;
  enabled: boolean;
}

export interface CarrierCompanySearchState {
  results: CarrierSearchResult[];
  isLoadingFirstPage: boolean;
  isLoadingMorePages: boolean;
  hasMore: boolean;
  loadMore: () => void;
  error: string | null;
  /** True once the query has settled and matched nothing. */
  isEmptyResult: boolean;
}

function buildSearchUrl(companyName: string, offset: number): string {
  const params = new URLSearchParams({
    name: companyName,
    limit: String(CARRIER_SEARCH_PAGE_LIMIT),
    offset: String(offset),
  });
  return `/companies/carriers/profiles?${params.toString()}`;
}

export function useCarrierCompanySearch(
  props: UseCarrierCompanySearchProps,
): CarrierCompanySearchState {
  const { searchValue, enabled } = props;

  const debouncedSearchValue = useDebounce({
    value: searchValue,
    delay: CARRIER_SEARCH_DEBOUNCE_MS,
  });
  const trimmedSearchValue = debouncedSearchValue.trim();
  const isDebouncing = searchValue.trim() !== trimmedSearchValue;

  /*
  The search term is part of the key, so a new term starts a new page list.
  Returning null stops paging: once for a query that should not run at all, and
  once when the previous page reported it was the last.
  */
  const getKey = useCallback(
    (pageIndex: number, previousPage: CarrierSearchResponse | null) => {
      if (!enabled || trimmedSearchValue === "") {
        return null;
      }
      if (pageIndex === 0) {
        return buildSearchUrl(trimmedSearchValue, 0);
      }
      // Every later page continues from the offset the page before it handed
      // back. A null offset means that page was the last one.
      if (previousPage?.next_offset == null) {
        return null;
      }
      return buildSearchUrl(trimmedSearchValue, previousPage.next_offset);
    },
    [enabled, trimmedSearchValue],
  );

  const { pages, size, setSize, loading, validating, error } =
    useSwrInfiniteFetch<CarrierSearchResponse>({
      getKey,
      errorMessage: SEARCH_ERROR_MESSAGE,
    });

  const results = pages.flatMap((page) => page.results);
  const lastPage = pages.at(-1);

  const loadMore = useCallback(() => {
    setSize((currentSize) => currentSize + 1);
  }, [setSize]);

  // More pages requested than loaded separates "appending the next page" from
  // "first load", which the dropdown renders differently: a spinner under the
  // list versus in place of it.
  const isLoadingMorePages = validating && size > pages.length;
  const isLoadingFirstPage =
    trimmedSearchValue !== "" && (isDebouncing || (loading && size === 1));

  return {
    results,
    isLoadingFirstPage,
    isLoadingMorePages,
    hasMore: lastPage != null && lastPage.next_offset !== null,
    loadMore,
    error,
    isEmptyResult:
      !isLoadingFirstPage && lastPage != null && results.length === 0,
  };
}
